import { Collapse, FormControlLabel, FormHelperText, Stack, Switch } from '@mui/material';
import {
  FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS,
  FlowHumanTaskTool,
  type FlowHumanTaskTools,
  FlowHumanTaskType,
} from 'flow-schema';

import { isDefined } from 'common';

import { ClinicalQuestionAssistantConfig } from './ClinicalQuestionAssistant/Config';
import { DocumentPacketGeneratorConfig } from './DocumentPacketGeneratorConfig';
import { useReactFlowViewContext } from '../../../../context/context';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

import HumanTaskTool = FlowHumanTaskTool;

type ToolsConfig = NonNullable<FlowHumanTaskTools['config']>;

/**
 * Tools tab for HumanTask block configuration.
 */
export function HumanTaskToolsFormFields() {
  const { isEditable } = useReactFlowViewContext();
  const { value: taskType } = useSelectedNodeField<FlowHumanTaskType>('config.taskType');
  const { value: enabledTools, setValue: setEnabledTools } = useSelectedNodeField<HumanTaskTool[]>(
    'config.tools.enabledTools'
  );
  const { value: toolsConfig, setValue: setToolsConfig } =
    useSelectedNodeField<ToolsConfig>('config.tools.config');

  const tools = enabledTools ?? FLOW_DEFAULT_ENABLED_HUMAN_TASK_TOOLS;

  return (
    <Stack gap={2}>
      <Stack gap={2}>
        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={tools.includes(HumanTaskTool.DocumentPacketGenerator)}
                disabled={!isEditable}
                onChange={() => {
                  if (tools.includes(HumanTaskTool.DocumentPacketGenerator)) {
                    setEnabledTools(
                      tools.filter(
                        (enabledTool) => enabledTool !== HumanTaskTool.DocumentPacketGenerator
                      )
                    );
                    if (isDefined(toolsConfig?.documentPacketGenerator)) {
                      setToolsConfig({ ...toolsConfig, documentPacketGenerator: undefined });
                    }
                    return;
                  }
                  setEnabledTools([...tools, HumanTaskTool.DocumentPacketGenerator]);
                }}
              />
            }
            label="Document Packet Generator"
          />
          <FormHelperText sx={{ marginLeft: 6 }}>
            Assemble, preview, and download clinical document packets (PDF).
          </FormHelperText>
        </Stack>
        <Collapse in={tools.includes(HumanTaskTool.DocumentPacketGenerator)}>
          <DocumentPacketGeneratorConfig />
        </Collapse>
      </Stack>
      {(taskType === FlowHumanTaskType.SubmitPriorAuthorization ||
        taskType === FlowHumanTaskType.CustomSubtask) && (
        <Stack gap={2}>
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    taskType === FlowHumanTaskType.SubmitPriorAuthorization ||
                    tools.includes(HumanTaskTool.ClinicalQuestionAssistant)
                  }
                  disabled={taskType === FlowHumanTaskType.SubmitPriorAuthorization || !isEditable}
                  onChange={() => {
                    if (taskType === FlowHumanTaskType.SubmitPriorAuthorization) {
                      return;
                    }
                    if (tools.includes(HumanTaskTool.ClinicalQuestionAssistant)) {
                      setEnabledTools(
                        tools.filter(
                          (enabledTool) => enabledTool !== HumanTaskTool.ClinicalQuestionAssistant
                        )
                      );
                      return;
                    }
                    setEnabledTools([...tools, HumanTaskTool.ClinicalQuestionAssistant]);
                  }}
                />
              }
              label="Clinical Question Assistant"
            />
            <FormHelperText sx={{ marginLeft: 6 }}>
              Use clinical context to help answer questions encountered while completing the task.
            </FormHelperText>
          </Stack>
          {taskType === FlowHumanTaskType.SubmitPriorAuthorization ? (
            <ClinicalQuestionAssistantConfig taskType={taskType} />
          ) : (
            <Collapse in={tools.includes(HumanTaskTool.ClinicalQuestionAssistant)}>
              <ClinicalQuestionAssistantConfig taskType={taskType} />
            </Collapse>
          )}
        </Stack>
      )}
    </Stack>
  );
}
